import {
  upsertConsultationServiceWorkflowData,
  getActiveConsentTemplates,
  upsertConsultationConsent,
  saveConsultationHistory,
  saveConsultationVitals,
  saveConsultationComplaint,
  saveConsultationComplaintTags,
  saveConsultationQA,
  saveConsultationUrineDipstick,
  saveConsultationDiagnosis,
  saveConsultationProblems,
  saveConsultationTreatmentPlan,
  saveConsultationFollowup,
} from "../../../../api/clinic/consultationApi.js";

export const saveWorkflowDrafts = async (consultationId, workflowDrafts) => {
  if (!workflowDrafts || typeof workflowDrafts !== 'object' || !Object.keys(workflowDrafts).length) return;

  for (const [serviceId, formdata] of Object.entries(workflowDrafts)) {
    if (!serviceId) continue;
    if (!formdata || typeof formdata !== 'object' || Object.keys(formdata).length === 0) continue;

    await upsertConsultationServiceWorkflowData(consultationId, {
      serviceid: serviceId,
      workflowtype: 'SERVICE_WORKFLOW',
      formdata,
      status: 'Final',
    });
  }
};

export const saveConsentDrafts = async (consultationId, consentDrafts) => {
  if (!consentDrafts || typeof consentDrafts !== "object") return;

  const templates = await getActiveConsentTemplates();
  const byCode = new Map(
    (templates || [])
      .filter((t) => t?.code)
      .map((t) => [String(t.code).toLowerCase(), t])
  );

  for (const [code, consent] of Object.entries(consentDrafts)) {
    const tmpl = byCode.get(String(code).toLowerCase());
    if (!tmpl?.templateid) continue;

    await upsertConsultationConsent(consultationId, {
      templateid: tmpl.templateid,
      answers: consent?.answers ?? consent ?? null,
      patientSigned: !!consent?.patientSigned,
      patientSignedAt: consent?.patientSignedAt ?? null,
      practitionerSigned: !!consent?.practitionerSigned,
      practitionerSignedAt: consent?.practitionerSignedAt ?? null,
    });
  }
};

export const saveHistoryDrafts = async (consultationId, history) => {
  if (!history) return;

  await saveConsultationHistory(consultationId, {
    familyhistory: history.familyHistory || null,
    medicalhistory: history.medicalHistory || null,
    surgicalhistory: history.surgicalHistory || null,
    immunitystatus: history.immunityStatus || null,
    alcohol: history.alcohol || null,
    caffeine: history.caffeine || null,
    smoking: history.smoking || null,
    social: history.social || null,
    travel: history.travel || null,
    contraception: history.contraception || null,
    porphyria: !!history.porphyria,
    porphyriatype: history.porphyria ? (history.porphyriaType || null) : null,
  });
};

export const saveVitalsDrafts = async (consultationId, vitals) => {
  if (!vitals) return;

  await saveConsultationVitals(consultationId, {
    temperature: vitals.temperature === "" ? null : Number(vitals.temperature),
    pulse: vitals.pulse === "" ? null : Number(vitals.pulse),
    respiration: vitals.respiration === "" ? null : Number(vitals.respiration),
    glucose: vitals.glucose === "" ? null : Number(vitals.glucose),
    bpsystolic: vitals.bpSystolic === "" ? null : Number(vitals.bpSystolic),
    bpdiastolic: vitals.bpDiastolic === "" ? null : Number(vitals.bpDiastolic),
    weight: vitals.weight === "" ? null : Number(vitals.weight),
    height: vitals.height === "" ? null : Number(vitals.height),
    bmi: vitals.bmi === "" ? null : Number(vitals.bmi),
    cholesterol: vitals.cholesterol === "" ? null : Number(vitals.cholesterol),
    hemoglobin: vitals.hemoglobin === "" ? null : Number(vitals.hemoglobin),
    hydration: !!vitals?.observations?.hydration,
    jaundice: !!vitals?.observations?.jaundice,
    anaemia: !!vitals?.observations?.anaemia,
    cyanosis: !!vitals?.observations?.cyanosis,
    clubbing: !!vitals?.observations?.clubbing,
    oedema: !!vitals?.observations?.oedema,
    lymphnodes: !!vitals?.observations?.lymphNodes,
  });
};

export const saveSymptomsDrafts = async (consultationId, symptoms) => {
  if (!symptoms) return;

  await saveConsultationComplaint(consultationId, {
    maincomplaint: symptoms.mainComplaint || null,
    symptomsreported: symptoms.symptomsReported || null,
    examinationfindings: symptoms.examinationFindings || null,
  });
  await saveConsultationComplaintTags(consultationId, symptoms.selectedComplaints || []);
  await saveConsultationQA(
    consultationId,
    (symptoms.qaPairs || []).map((p, idx) => ({
      question: p.question,
      answer: p.answer,
      sortorder: idx + 1,
    }))
  );
};

export const saveTestsDrafts = async (consultationId, tests) => {
  if (!tests) return;

  await saveConsultationUrineDipstick(consultationId, {
    protein: tests.protein || null,
    blood: tests.blood || null,
    ketones: tests.ketones || null,
    specificgravity: tests.specificGravity || null,
    urobilinogen: tests.urobilinogen || null,
    leucocytes: tests.leucocytes || null,
    ph: tests.ph || null,
    bilirubin: tests.bilirubin || null,
    observations: tests.observations || null,
  });
};

export const saveDiagnosisDrafts = async (consultationId, diag) => {
  if (!diag) return;

  await saveConsultationDiagnosis(
    consultationId,
    (diag.selectedDiagnoses || []).map((d, idx) => ({
      icd10code: d.code || null,
      icd10description: d.description || null,
      isprimary: idx === 0 ? true : !!d.isprimary,
    }))
  );
};

export const saveProblemsDrafts = async (consultationId, problems) => {
  if (!problems) return;

  await saveConsultationProblems(
    consultationId,
    (problems.problems || []).map((p) => ({
      name: p.name,
      problemdate: p.date || null,
      status: p.status || null,
      color: p.color || null,
    }))
  );
};

export const saveTreatmentPlanDrafts = async (consultationId, plan) => {
  if (!plan) return;

  await saveConsultationTreatmentPlan(consultationId, {
    lifestyleadvice: plan.lifestyleAdvice || null,
    procedures: plan.procedures || null,
    notes: plan.notes || null,
    patientinstructions: plan.patientInstructions || null,
  });
};

export const saveFollowupDrafts = async (consultationId, followup) => {
  if (!followup) return;

  await saveConsultationFollowup(consultationId, {
    followupdate: followup.followUpDate ? followup.followUpDate : null,
    followupnotes: followup.followUpNotes || null,
  });
};

export const saveAllDrafts = async (consultationId, drafts) => {
  await saveWorkflowDrafts(consultationId, drafts.serviceWorkflowData);
  await saveConsentDrafts(consultationId, drafts.consentForms);
  await saveHistoryDrafts(consultationId, drafts.medicalSocialHistory);
  await saveVitalsDrafts(consultationId, drafts.vitalsObservations);
  await saveSymptomsDrafts(consultationId, drafts.symptomsExam);
  await saveTestsDrafts(consultationId, drafts.specializedTests);
  await saveDiagnosisDrafts(consultationId, drafts.diagnosisICD10);
  await saveProblemsDrafts(consultationId, drafts.problemList);
  await saveTreatmentPlanDrafts(consultationId, drafts.treatmentPlan);
  await saveFollowupDrafts(consultationId, drafts.reviewFollowUp);
};
